import {
  Badge,
  type CellContext,
  type ColumnDef,
  cn,
  DataGrid,
  DataGridColumnHeader,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { ICompanyAppMapping } from "@models";
import { useCompanyAppsMapping } from "@query";
import { APP_BASE_PATH, DetailsPanel } from "@shared";
import { cva } from "class-variance-authority";
import { useCallback, useMemo, useState } from "react";

const applicationsHeaderVariants = cva(
  "text-foreground px-1 py-0.5 font-semibold text-sm",
);
const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
} as const;

interface IApplicationsProps {
  companyUuid: string | undefined;
}

export function Applications({ companyUuid }: IApplicationsProps) {
  const { t } = useTranslation();
  const [selectedApplication, setSelectedApplication] =
    useState<ICompanyAppMapping | null>(null);
  const { data: companyAppsMapping, isLoading } = useCompanyAppsMapping(
    companyUuid,
    APP_BASE_PATH,
  );
  const applicationsData = companyAppsMapping ?? [];

  const applicationColumns = useMemo<ColumnDef<ICompanyAppMapping, unknown>[]>(
    () => [
      {
        accessorKey: "cloudProvider",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.cloud_provider")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "region",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_subscribe.deployment_region")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "application",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.application")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "version",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.home.solution_version_label")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info: CellContext<ICompanyAppMapping, unknown>) => {
          const v = info.getValue() as string | undefined;
          return (
            <Badge variant="mono">
              {v ? (v.startsWith("v") ? v : `v${v}`) : "—"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "cluster",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.cluster")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "tenant",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.tenant")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "status",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
            className={cn(applicationsHeaderVariants())}
          />
        ),
        filterFn: (row, columnId, filterValue) => {
          const values = Array.isArray(filterValue) ? filterValue : [];
          if (values.length === 0) return true;
          return values.includes(row.getValue(columnId));
        },
        cell: (info: CellContext<ICompanyAppMapping, unknown>) => {
          const status = (info.getValue() as string) ?? "";
          const isActive = status.toLowerCase() === "updated";

          return (
            <Badge
              variant={isActive ? "success" : "neutral"}
              appearance="ghost"
              size="sm"
              dot>
              {status || "—"}
            </Badge>
          );
        },
      },
    ],
    [t],
  );

  const handleRowClick = useCallback((row: ICompanyAppMapping) => {
    setSelectedApplication(row);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedApplication(null);
  }, []);

  const detailItems = useMemo(() => {
    if (!selectedApplication) return [];
    return [
      {
        label: t("hub.administer.cloud_provider"),
        value: selectedApplication.cloudProvider ?? "—",
      },
      {
        label: t("hub.catalog_subscribe.deployment_region"),
        value: selectedApplication.region ?? "—",
      },
      {
        label: t("hub.administer.application"),
        value: selectedApplication.application ?? "—",
      },
      {
        label: t("hub.home.solution_version_label"),
        value: selectedApplication.version ?? "—",
      },
      {
        label: t("hub.administer.cluster"),
        value: selectedApplication.cluster ?? "—",
      },
      {
        label: t("hub.administer.tenant"),
        value: selectedApplication.tenant ?? "—",
      },
      {
        label: t("hub.administer.status"),
        value: selectedApplication.status ?? "—",
      },
    ];
  }, [selectedApplication, t]);

  return (
    <>
      <div className="flex-1 min-h-0">
        <DataGrid<ICompanyAppMapping>
          data={applicationsData}
          columns={applicationColumns}
          recordCount={applicationsData.length}
          loading={isLoading}
          onRowClick={handleRowClick}
          tableLayout={DATA_GRID_LAYOUT}
          withExport
          withSearch={{
            searchKey: "application",
            placeholder: t("hub.administer.search_results"),
          }}
          paginationPlacement="toolbar"
          paginationConfig={{
            showRowsPerPage: true,
          }}
        />
      </div>

      <DetailsPanel
        open={!!selectedApplication}
        onOpenChange={(open) => !open && handleClosePanel()}
        title={t("hub.administer.application_details")}
        items={detailItems}
        statusLabel={t("hub.administer.status")}
        cancelLabel={t("hub.administer.cancel")}
      />
    </>
  );
}
